import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { Sparkles, Download, FileImage, FileText, Loader2, Upload, Palette, Type, Move, RefreshCw, Image as ImageIcon, ImagePlus, Trash2, Copy as CopyIcon, Undo2, Redo2, RotateCw, Bold, Italic as ItalicIcon, Underline as UnderlineIcon, CaseUpper, RotateCcw, AlignLeft, AlignCenter, AlignRight, AlignJustify, Layers, Sliders, Droplet, Ruler } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import ofuesiLogo from "@/assets/ofuesi-logo.png";

// ---------------- Types ----------------
export interface FlyerEvent {
  id: string;
  region: string;
  region_id?: string | null;
  title: string;
  theme?: string | null;
  scripture?: string | null;
  venue_name?: string | null;
  venue_address?: string | null;
  city?: string | null;
  state?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  speaker_name?: string | null;
  speaker_title?: string | null;
  worship_team?: string | null;
  coordinator_name?: string | null;
  coordinator_email?: string | null;
  coordinator_phone?: string | null;
  registration_url?: string | null;
}

interface ElementStyle {
  x: number; y: number; // percent of canvas
  width: number; // percent
  fontSize: number;
  fontFamily: string;
  fontWeight: number;
  color: string;
  colorOpacity?: number; // 0..1
  align: "left" | "center" | "right" | "justify";
  vAlign?: "top" | "center" | "bottom";
  bg?: string; // optional pill bg
  italic?: boolean;
  underline?: boolean;
  uppercase?: boolean;
  lineHeight?: number; // multiplier
  letterSpacing?: number;
  shadow?: boolean;
  paddingTop?: number; // px
  paddingBottom?: number; // px
  maxLines?: number; // 0/undef = unlimited
  autoFit?: boolean; // shrink to fit width/maxLines
}

type ElKey = "eyebrow" | "title" | "theme" | "scripture" | "date" | "venue" | "speaker" | "cta" | "footer";

interface LogoState {
  src: string;
  visible: boolean;
  x: number; y: number; // percent (center-anchored)
  size: number; // base width as % of canvas
  scaleX: number; // 0.2..3
  scaleY: number; // 0.2..3
  rotation: number; // degrees
  opacity: number; // 0..1
}

interface ImageItem {
  id: string;
  src: string;
  x: number; y: number; // percent (center-anchored)
  width: number; // % of canvas
  scaleX: number;
  scaleY: number;
  rotation: number; // degrees
  opacity: number; // 0..1
  borderRadius: number; // px
}

interface FlyerState {
  templateId: string;
  bgImage?: string; // dataURL or http
  bgOverlay: number; // 0..0.8 dark overlay opacity
  accentColor: string;
  elements: Record<ElKey, ElementStyle & { visible: boolean; text?: string }>;
  logo: LogoState;
  images: ImageItem[];
}

// ---------------- Templates ----------------
const FONTS = [
  "Inter, system-ui, sans-serif",
  "'Playfair Display', Georgia, serif",
  "Georgia, 'Times New Roman', serif",
  "'Cormorant Garamond', Georgia, serif",
  "'Montserrat', system-ui, sans-serif",
  "'Bebas Neue', Impact, sans-serif",
  "'Poppins', system-ui, sans-serif",
  "'Merriweather', Georgia, serif",
];

interface Template {
  id: string;
  name: string;
  preview: string; // gradient preview
  bgGradient: string; // CSS background for canvas when no image
  defaultAccent: string;
  defaultBgImage?: string;
  styleHint: string; // for AI bg generation
  elements: Record<ElKey, ElementStyle & { visible: boolean }>;
  defaultLogo?: Partial<LogoState>;
}

const baseEl = (overrides: Partial<ElementStyle> = {}): ElementStyle & { visible: boolean } => ({
  x: 50, y: 50, width: 80, fontSize: 18, fontFamily: FONTS[0], fontWeight: 500,
  color: "#ffffff", align: "center", visible: true, ...overrides,
});

const TEMPLATES: Template[] = [
  {
    id: "majestic",
    name: "Majestic Gold",
    preview: "linear-gradient(135deg,#0a1f3d 0%,#1d4078 50%,#c9a84c 100%)",
    bgGradient: "radial-gradient(ellipse at top,#1d4078 0%,#0a1f3d 70%),linear-gradient(180deg,#0a1f3d,#000)",
    defaultAccent: "#c9a84c",
    styleHint: "majestic golden light rays from heaven, deep navy night sky with subtle stars, painterly cinematic, baroque elegance",
    elements: {
      eyebrow: baseEl({ x: 50, y: 8, width: 70, fontSize: 12, letterSpacing: 4, color: "#c9a84c", fontWeight: 600 }),
      title: baseEl({ x: 50, y: 22, width: 86, fontSize: 56, fontFamily: FONTS[1], fontWeight: 700, color: "#ffffff", shadow: true }),
      theme: baseEl({ x: 50, y: 42, width: 78, fontSize: 20, fontFamily: FONTS[1], italic: true, color: "#f5e6b8" }),
      scripture: baseEl({ x: 50, y: 51, width: 70, fontSize: 13, color: "#e8d9a8", italic: true }),
      date: baseEl({ x: 50, y: 64, width: 80, fontSize: 22, fontWeight: 700, color: "#ffffff" }),
      venue: baseEl({ x: 50, y: 72, width: 80, fontSize: 14, color: "#e2e8f0" }),
      speaker: baseEl({ x: 50, y: 80, width: 80, fontSize: 15, color: "#c9a84c", fontWeight: 600 }),
      cta: baseEl({ x: 50, y: 89, width: 60, fontSize: 14, color: "#0a1f3d", bg: "#c9a84c", fontWeight: 700 }),
      footer: baseEl({ x: 50, y: 96, width: 90, fontSize: 9, color: "#94a3b8", letterSpacing: 2 }),
    },
    defaultLogo: { visible: true, x: 50, y: 6, size: 22, scaleX: 1, scaleY: 1, rotation: 0, opacity: 0.95 },
  },
  {
    id: "ethereal",
    name: "Ethereal Light",
    preview: "linear-gradient(135deg,#fef3c7 0%,#fbbf24 40%,#7c3aed 100%)",
    bgGradient: "linear-gradient(180deg,#fef9e7 0%,#fde68a 50%,#a78bfa 100%)",
    defaultAccent: "#7c3aed",
    styleHint: "ethereal sunrise over clouds, soft pastel watercolor, golden hour glow, gentle dove silhouettes, peaceful and divine",
    elements: {
      eyebrow: baseEl({ x: 50, y: 8, width: 70, fontSize: 11, letterSpacing: 5, color: "#5b21b6", fontWeight: 700 }),
      title: baseEl({ x: 50, y: 20, width: 86, fontSize: 52, fontFamily: FONTS[1], fontWeight: 700, color: "#1e1b4b" }),
      theme: baseEl({ x: 50, y: 40, width: 78, fontSize: 19, fontFamily: FONTS[3], italic: true, color: "#5b21b6" }),
      scripture: baseEl({ x: 50, y: 49, width: 70, fontSize: 13, color: "#4c1d95" }),
      date: baseEl({ x: 50, y: 64, width: 80, fontSize: 22, fontWeight: 700, color: "#1e1b4b" }),
      venue: baseEl({ x: 50, y: 72, width: 80, fontSize: 14, color: "#312e81" }),
      speaker: baseEl({ x: 50, y: 80, width: 80, fontSize: 15, color: "#7c3aed", fontWeight: 600 }),
      cta: baseEl({ x: 50, y: 89, width: 60, fontSize: 14, color: "#ffffff", bg: "#7c3aed", fontWeight: 700 }),
      footer: baseEl({ x: 50, y: 96, width: 90, fontSize: 9, color: "#6d28d9", letterSpacing: 2 }),
    },
    defaultLogo: { visible: true, x: 50, y: 6, size: 24, scaleX: 1, scaleY: 1, rotation: 0, opacity: 1 },
  },
  {
    id: "modern-bold",
    name: "Modern Bold",
    preview: "linear-gradient(135deg,#0f172a 0%,#1e293b 50%,#06b6d4 100%)",
    bgGradient: "linear-gradient(135deg,#0f172a 0%,#1e293b 60%,#0e7490 100%)",
    defaultAccent: "#06b6d4",
    styleHint: "modern abstract geometric shapes, electric cyan and teal gradients, contemporary worship vibe, bold minimalism",
    elements: {
      eyebrow: baseEl({ x: 10, y: 8, width: 50, fontSize: 11, letterSpacing: 4, color: "#06b6d4", fontWeight: 700, align: "left" }),
      title: baseEl({ x: 10, y: 18, width: 80, fontSize: 64, fontFamily: FONTS[5], fontWeight: 900, color: "#ffffff", align: "left" }),
      theme: baseEl({ x: 10, y: 40, width: 70, fontSize: 18, color: "#67e8f9", align: "left", italic: true }),
      scripture: baseEl({ x: 10, y: 49, width: 70, fontSize: 12, color: "#cbd5e1", align: "left" }),
      date: baseEl({ x: 10, y: 64, width: 80, fontSize: 26, fontWeight: 800, color: "#ffffff", align: "left" }),
      venue: baseEl({ x: 10, y: 73, width: 80, fontSize: 14, color: "#e2e8f0", align: "left" }),
      speaker: baseEl({ x: 10, y: 81, width: 80, fontSize: 15, color: "#06b6d4", fontWeight: 700, align: "left" }),
      cta: baseEl({ x: 10, y: 90, width: 50, fontSize: 14, color: "#0f172a", bg: "#06b6d4", fontWeight: 800, align: "left" }),
      footer: baseEl({ x: 10, y: 96, width: 90, fontSize: 9, color: "#64748b", letterSpacing: 2, align: "left" }),
    },
    defaultLogo: { visible: true, x: 88, y: 8, size: 16, scaleX: 1, scaleY: 1, rotation: 0, opacity: 1 },
  },
  {
    id: "warm-revival",
    name: "Warm Revival",
    preview: "linear-gradient(135deg,#7c2d12 0%,#dc2626 50%,#fbbf24 100%)",
    bgGradient: "radial-gradient(ellipse at bottom,#fbbf24 0%,#dc2626 50%,#7c2d12 100%)",
    defaultAccent: "#fbbf24",
    styleHint: "warm fire glow, revival flames in soft abstract painterly style, golden embers, sunset orange and crimson, hopeful",
    elements: {
      eyebrow: baseEl({ x: 50, y: 8, width: 70, fontSize: 12, letterSpacing: 4, color: "#fde68a", fontWeight: 600 }),
      title: baseEl({ x: 50, y: 22, width: 86, fontSize: 58, fontFamily: FONTS[1], fontWeight: 800, color: "#fffbeb", shadow: true }),
      theme: baseEl({ x: 50, y: 42, width: 78, fontSize: 20, fontFamily: FONTS[7], italic: true, color: "#fde68a" }),
      scripture: baseEl({ x: 50, y: 51, width: 70, fontSize: 13, color: "#fef3c7", italic: true }),
      date: baseEl({ x: 50, y: 64, width: 80, fontSize: 24, fontWeight: 700, color: "#fffbeb" }),
      venue: baseEl({ x: 50, y: 72, width: 80, fontSize: 14, color: "#fef3c7" }),
      speaker: baseEl({ x: 50, y: 80, width: 80, fontSize: 15, color: "#fbbf24", fontWeight: 700 }),
      cta: baseEl({ x: 50, y: 89, width: 60, fontSize: 14, color: "#7c2d12", bg: "#fbbf24", fontWeight: 800 }),
      footer: baseEl({ x: 50, y: 96, width: 90, fontSize: 9, color: "#fed7aa", letterSpacing: 2 }),
    },
    defaultLogo: { visible: true, x: 50, y: 6, size: 22, scaleX: 1, scaleY: 1, rotation: 0, opacity: 0.95 },
  },
  {
    id: "minimal-white",
    name: "Minimal White",
    preview: "linear-gradient(135deg,#ffffff 0%,#f3f4f6 60%,#006B6B 100%)",
    bgGradient: "linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)",
    defaultAccent: "#006B6B",
    styleHint: "minimal white space, single elegant botanical line illustration, serene editorial aesthetic, soft shadow",
    elements: {
      eyebrow: baseEl({ x: 50, y: 8, width: 70, fontSize: 11, letterSpacing: 6, color: "#006B6B", fontWeight: 700 }),
      title: baseEl({ x: 50, y: 22, width: 86, fontSize: 54, fontFamily: FONTS[1], fontWeight: 700, color: "#0f172a" }),
      theme: baseEl({ x: 50, y: 42, width: 78, fontSize: 18, fontFamily: FONTS[3], italic: true, color: "#475569" }),
      scripture: baseEl({ x: 50, y: 50, width: 70, fontSize: 12, color: "#64748b" }),
      date: baseEl({ x: 50, y: 64, width: 80, fontSize: 22, fontWeight: 700, color: "#0f172a" }),
      venue: baseEl({ x: 50, y: 72, width: 80, fontSize: 14, color: "#334155" }),
      speaker: baseEl({ x: 50, y: 80, width: 80, fontSize: 15, color: "#006B6B", fontWeight: 600 }),
      cta: baseEl({ x: 50, y: 89, width: 60, fontSize: 14, color: "#ffffff", bg: "#006B6B", fontWeight: 700 }),
      footer: baseEl({ x: 50, y: 96, width: 90, fontSize: 9, color: "#94a3b8", letterSpacing: 2 }),
    },
    defaultLogo: { visible: true, x: 50, y: 6, size: 26, scaleX: 1, scaleY: 1, rotation: 0, opacity: 1 },
  },
  {
    id: "stained-glass",
    name: "Stained Glass",
    preview: "linear-gradient(135deg,#1e3a8a 0%,#7e22ce 50%,#db2777 100%)",
    bgGradient: "linear-gradient(135deg,#1e3a8a 0%,#7e22ce 50%,#831843 100%)",
    defaultAccent: "#fbbf24",
    styleHint: "stained glass cathedral window abstract, jewel tones sapphire ruby amethyst, light streaming through, sacred art",
    elements: {
      eyebrow: baseEl({ x: 50, y: 8, width: 70, fontSize: 12, letterSpacing: 4, color: "#fbbf24", fontWeight: 600 }),
      title: baseEl({ x: 50, y: 22, width: 86, fontSize: 56, fontFamily: FONTS[1], fontWeight: 700, color: "#ffffff", shadow: true }),
      theme: baseEl({ x: 50, y: 42, width: 78, fontSize: 20, fontFamily: FONTS[3], italic: true, color: "#fde68a" }),
      scripture: baseEl({ x: 50, y: 51, width: 70, fontSize: 13, color: "#fbcfe8", italic: true }),
      date: baseEl({ x: 50, y: 64, width: 80, fontSize: 22, fontWeight: 700, color: "#ffffff" }),
      venue: baseEl({ x: 50, y: 72, width: 80, fontSize: 14, color: "#e9d5ff" }),
      speaker: baseEl({ x: 50, y: 80, width: 80, fontSize: 15, color: "#fbbf24", fontWeight: 700 }),
      cta: baseEl({ x: 50, y: 89, width: 60, fontSize: 14, color: "#1e1b4b", bg: "#fbbf24", fontWeight: 800 }),
      footer: baseEl({ x: 50, y: 96, width: 90, fontSize: 9, color: "#c4b5fd", letterSpacing: 2 }),
    },
    defaultLogo: { visible: true, x: 50, y: 6, size: 22, scaleX: 1, scaleY: 1, rotation: 0, opacity: 0.95 },
  },
];

// ---------------- Helpers ----------------
const slugify = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const hexToRgba = (hex: string, alpha = 1): string => {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  if (hex.startsWith("rgba") || hex.startsWith("rgb")) return hex;
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const buildText = (key: ElKey, e: FlyerEvent, regUrl: string): string => {
  switch (key) {
    case "eyebrow": return `✦  OFUESI ${(e.region || "").toUpperCase()} CONFERENCE  ✦`;
    case "title": return e.title || "Conference Title";
    case "theme": return e.theme ? `"${e.theme}"` : "";
    case "scripture": return e.scripture || "";
    case "date": {
      const d = e.start_date ? new Date(e.start_date).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "Date TBA";
      const t = e.start_time ? ` · ${e.start_time}${e.end_time ? `–${e.end_time}` : ""}` : "";
      return d + t;
    }
    case "venue": return [e.venue_name, e.city, e.state].filter(Boolean).join(", ") || "Venue TBA";
    case "speaker": return e.speaker_name ? `Featuring ${e.speaker_name}${e.speaker_title ? `, ${e.speaker_title}` : ""}` : "";
    case "cta": return `REGISTER  →  ${regUrl.replace(/^https?:\/\//, "")}`;
    case "footer": return `HOSTED BY OFUESI · ${(e.region || "").toUpperCase()} REGION · OFUESI.ORG · ZELLE TO OFUESI ORG ACCOUNT ONLY · 1 COR 14:40`;
  }
};

const DEFAULT_LOGO: LogoState = {
  src: ofuesiLogo, visible: true, x: 50, y: 6, size: 26,
  scaleX: 1, scaleY: 1, rotation: 0, opacity: 1,
};

const initState = (tpl: Template): FlyerState => ({
  templateId: tpl.id,
  bgOverlay: 0.25,
  accentColor: tpl.defaultAccent,
  elements: Object.fromEntries(
    (Object.keys(tpl.elements) as ElKey[]).map(k => [k, { ...tpl.elements[k], visible: true }])
  ) as FlyerState["elements"],
  logo: { ...DEFAULT_LOGO, ...(tpl.defaultLogo || {}) },
  images: [],
});

// Snap targets (percent) for canvas alignment guides
const SNAP_TARGETS = [0, 10, 25, 33.333, 50, 66.667, 75, 90, 100];
const SNAP_THRESHOLD = 1.6; // percent
const snap = (v: number): { value: number; hit: number | null } => {
  for (const t of SNAP_TARGETS) {
    if (Math.abs(v - t) <= SNAP_THRESHOLD) return { value: t, hit: t };
  }
  return { value: v, hit: null };
};

// ---------------- Main Component ----------------
export const FlyerBuilderPro: React.FC<{ event: FlyerEvent }> = ({ event }) => {
  const [tplIdx, setTplIdx] = useState(0);
  const tpl = TEMPLATES[tplIdx];
  const [state, setState] = useState<FlyerState>(() => initState(TEMPLATES[0]));
  const [aiBgLoading, setAiBgLoading] = useState(false);
  const [aiCopyLoading, setAiCopyLoading] = useState(false);
  const [selected, setSelected] = useState<ElKey | null>("title");
  const [customStyle, setCustomStyle] = useState("");
  const [evt, setEvt] = useState<FlyerEvent>(event);
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const logoFileRef = useRef<HTMLInputElement>(null);
  const imageFileRef = useRef<HTMLInputElement>(null);
  const elRefs = useRef<Partial<Record<ElKey, HTMLDivElement | null>>>({});
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);

  // Full-state Undo/Redo
  const [past, setPast] = useState<FlyerState[]>([]);
  const [future, setFuture] = useState<FlyerState[]>([]);
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);
  const lastCommitRef = useRef<{ ts: number; tag: string }>({ ts: 0, tag: "" });

  const commit = (tag = "edit") => {
    // coalesce rapid edits with same tag (e.g., textarea typing)
    const now = Date.now();
    if (lastCommitRef.current.tag === tag && now - lastCommitRef.current.ts < 500) return;
    lastCommitRef.current = { ts: now, tag };
    setPast(p => [...p.slice(-49), stateRef.current]);
    setFuture([]);
  };
  const undo = () => {
    setPast(p => {
      if (!p.length) return p;
      const prev = p[p.length - 1];
      setFuture(f => [stateRef.current, ...f].slice(0, 50));
      setState(prev);
      return p.slice(0, -1);
    });
    lastCommitRef.current = { ts: 0, tag: "" };
  };
  const redo = () => {
    setFuture(f => {
      if (!f.length) return f;
      const next = f[0];
      setPast(p => [...p, stateRef.current].slice(-50));
      setState(next);
      return f.slice(1);
    });
    lastCommitRef.current = { ts: 0, tag: "" };
  };
  // Backwards-compat aliases for existing logo undo callers
  const commitLogo = (_: LogoState) => commit("logo");
  const undoLogo = undo;
  const redoLogo = redo;
  const logoPast = past;
  const logoFuture = future;

  // Snap guides during logo drag
  const [snapGuides, setSnapGuides] = useState<{ vx: number | null; hy: number | null }>({ vx: null, hy: null });

  useEffect(() => { setEvt(event); }, [event.id]);

  // Keyboard undo/redo
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((k === "z" && e.shiftKey) || k === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Auto-fit text: shrink fontSize until content fits its width and (if set) maxLines.
  useLayoutEffect(() => {
    (Object.keys(state.elements) as ElKey[]).forEach((k) => {
      const node = elRefs.current[k];
      const el = state.elements[k];
      if (!node) return;
      // Reset to base before measuring
      node.style.fontSize = `${el.fontSize}px`;
      if (!el.autoFit) return;
      const lh = el.lineHeight ?? 1.2;
      let size = el.fontSize;
      const minSize = 6;
      const lineHeightPx = () => size * lh;
      const fits = () => {
        const noOverflowH = node.scrollWidth <= node.clientWidth + 1;
        if (!el.maxLines) return noOverflowH;
        const maxH = Math.ceil(lineHeightPx() * el.maxLines + (el.paddingTop ?? 0) + (el.paddingBottom ?? 0));
        return noOverflowH && node.scrollHeight <= maxH + 1;
      };
      let guard = 60;
      while (size > minSize && !fits() && guard-- > 0) {
        size = Math.max(minSize, size - 1);
        node.style.fontSize = `${size}px`;
      }
    });
  });

  const switchTemplate = (idx: number) => {
    commit("template");
    setTplIdx(idx);
    setState(prev => {
      const fresh = initState(TEMPLATES[idx]);
      return {
        ...fresh,
        bgImage: prev.bgImage,
        logo: { ...fresh.logo, src: prev.logo.src || fresh.logo.src },
        images: prev.images,
      };
    });
  };

  const regionSlug = slugify(evt.region || "region");
  const yearSlug = evt.start_date ? new Date(evt.start_date).getFullYear() : new Date().getFullYear();
  const regSlug = `${regionSlug}-${yearSlug}`;
  const baseRegUrl = evt.registration_url || `https://ofuesi.org/register/${regSlug}`;
  // Per-template tracking so each flyer template is a distinct campaign source
  const tplSlug = slugify(TEMPLATES[tplIdx]?.id || "default");
  const trackingSrc = `flyer_${regionSlug}_${yearSlug}_${tplSlug}`;
  const utmParams = `utm_source=flyer&utm_medium=flyer_${tplSlug}&utm_campaign=${regionSlug}_${yearSlug}&src=${trackingSrc}`;
  const regUrl = baseRegUrl + (baseRegUrl.includes("?") ? "&" : "?") + utmParams;

  // updateEl: snapshot history per intent (coalesces typing).
  // For text edits we tag with the field key so per-element typing groups together.
  const updateEl = (k: ElKey, patch: Partial<ElementStyle & { visible: boolean; text?: string }>, opts?: { tag?: string }) => {
    const tag = opts?.tag ?? (typeof patch.text === "string" ? `text:${k}` : `el:${k}`);
    commit(tag);
    setState(s => ({ ...s, elements: { ...s.elements, [k]: { ...s.elements[k], ...patch } } }));
  };

  // ---- Drag handling ----
  const dragRef = useRef<{ key: ElKey; startX: number; startY: number; origX: number; origY: number; committed: boolean } | null>(null);
  const onDragStart = (e: React.PointerEvent, key: ElKey) => {
    e.stopPropagation();
    setSelected(key);
    const el = state.elements[key];
    dragRef.current = { key, startX: e.clientX, startY: e.clientY, origX: el.x, origY: el.y, committed: false };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent) => {
    if (!dragRef.current || !canvasRef.current) return;
    if (!dragRef.current.committed) { commit(`drag:${dragRef.current.key}`); dragRef.current.committed = true; }
    const rect = canvasRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragRef.current.startX) / rect.width) * 100;
    const dy = ((e.clientY - dragRef.current.startY) / rect.height) * 100;
    const k = dragRef.current.key;
    setState(s => ({ ...s, elements: { ...s.elements, [k]: { ...s.elements[k], x: Math.max(0, Math.min(100, dragRef.current!.origX + dx)), y: Math.max(0, Math.min(100, dragRef.current!.origY + dy)) } } }));
  };
  const onDragEnd = () => { dragRef.current = null; };

  // ---- AI background ----
  const generateAIBg = async () => {
    setAiBgLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-flyer-background", {
        body: { theme: evt.theme, title: evt.title, scripture: evt.scripture, style: customStyle || tpl.styleHint },
      });
      if (error) throw error;
      const url = (data as any)?.imageUrl;
      if (!url) throw new Error("No image returned");
      setState(s => ({ ...s, bgImage: url }));
      toast({ title: "Background generated", description: "AI-generated background applied." });
    } catch (e: any) {
      toast({ title: "Generation failed", description: e.message, variant: "destructive" });
    }
    setAiBgLoading(false);
  };

  const generateAICopy = async () => {
    setAiCopyLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-flyer-copy", {
        body: {
          title: evt.title, theme: evt.theme, scripture: evt.scripture,
          speaker_name: evt.speaker_name, speaker_title: evt.speaker_title,
          venue: evt.venue_name, city: evt.city, state: evt.state,
          start_date: evt.start_date, end_date: evt.end_date, region: evt.region,
        },
      });
      if (error) throw error;
      const ai = data as any;
      setEvt(v => ({
        ...v,
        theme: ai?.theme_line || v.theme,
        scripture: ai?.scripture_suggestion || v.scripture,
      }));
      toast({ title: "Copy refined", description: ai?.tagline ? `Tagline: ${ai.tagline}` : "Theme & scripture refined." });
    } catch (e: any) {
      toast({ title: "AI failed", description: e.message, variant: "destructive" });
    }
    setAiCopyLoading(false);
  };

  // ---- AI design assistant (natural-language patches to FlyerState) ----
  const [aiDesignPrompt, setAiDesignPrompt] = useState("");
  const [aiDesignLoading, setAiDesignLoading] = useState(false);
  const [aiDesignNote, setAiDesignNote] = useState<string>("");
  const runAIDesign = async () => {
    if (!aiDesignPrompt.trim()) return;
    setAiDesignLoading(true);
    setAiDesignNote("");
    try {
      const elementsSummary: Record<string, { text?: string; visible: boolean }> = {};
      (Object.keys(state.elements) as ElKey[]).forEach((k) => {
        elementsSummary[k] = { text: state.elements[k].text, visible: state.elements[k].visible };
      });
      const { data, error } = await supabase.functions.invoke("ai-edit-flyer-design", {
        body: {
          region: evt.region,
          prompt: aiDesignPrompt,
          currentState: {
            elements: elementsSummary,
            accentColor: state.accentColor,
            bgOverlay: state.bgOverlay,
          },
        },
      });
      if (error) throw error;
      const patch = (data as any)?.patch || {};
      commit("ai-design");
      setState((s) => {
        const next: FlyerState = { ...s, elements: { ...s.elements } };
        if (patch.accentColor && /^#[0-9a-fA-F]{6}$/.test(patch.accentColor)) {
          next.accentColor = patch.accentColor;
        }
        if (typeof patch.bgOverlay === "number" && patch.bgOverlay >= 0 && patch.bgOverlay <= 0.8) {
          next.bgOverlay = patch.bgOverlay;
        }
        if (patch.texts && typeof patch.texts === "object") {
          for (const k of Object.keys(patch.texts)) {
            const key = k as ElKey;
            if (next.elements[key]) {
              next.elements[key] = { ...next.elements[key], text: String(patch.texts[k]) };
            }
          }
        }
        if (Array.isArray(patch.hide)) {
          patch.hide.forEach((k: string) => {
            const key = k as ElKey;
            if (next.elements[key]) next.elements[key] = { ...next.elements[key], visible: false };
          });
        }
        if (Array.isArray(patch.show)) {
          patch.show.forEach((k: string) => {
            const key = k as ElKey;
            if (next.elements[key]) next.elements[key] = { ...next.elements[key], visible: true };
          });
        }
        return next;
      });
      setAiDesignNote(typeof patch.notes === "string" ? patch.notes : "Applied AI changes. Use Undo (⌘Z) to revert.");
      // Optional background regeneration
      if (patch.regenerateBackground) {
        if (patch.backgroundStyle && typeof patch.backgroundStyle === "string") {
          setCustomStyle(patch.backgroundStyle);
        }
        // Fire-and-forget background generation
        generateAIBg();
      }
      setAiDesignPrompt("");
      toast({ title: "AI design applied", description: patch.notes || "Patch applied to flyer." });
    } catch (e: any) {
      toast({ title: "AI design failed", description: e.message || String(e), variant: "destructive" });
    } finally {
      setAiDesignLoading(false);
    }
  };


  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setState(s => ({ ...s, bgImage: reader.result as string }));
    reader.readAsDataURL(f);
  };

  const onLogoUpload = (_e: React.ChangeEvent<HTMLInputElement>) => {
    // OFUESI logo is locked — uploads are disabled to ensure brand consistency.
    setState(s => ({ ...s, logo: { ...s.logo, src: ofuesiLogo, visible: true } }));
  };


  const updateLogo = (patch: Partial<LogoState>) =>
    setState(s => ({ ...s, logo: { ...s.logo, ...patch } }));

  // Commit current logo to history (used by sliders' onValueCommit and drag end)
  const snapshotLogoForUndo = (snap: LogoState) => commitLogo(snap);

  // Logo drag
  const logoDragRef = useRef<{ startX: number; startY: number; origX: number; origY: number; preDrag: LogoState } | null>(null);
  const onLogoDragStart = (e: React.PointerEvent) => {
    e.stopPropagation();
    setSelected(null);
    logoDragRef.current = {
      startX: e.clientX, startY: e.clientY,
      origX: state.logo.x, origY: state.logo.y,
      preDrag: state.logo,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onLogoDragMove = (e: React.PointerEvent) => {
    if (!logoDragRef.current || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const dx = ((e.clientX - logoDragRef.current.startX) / rect.width) * 100;
    const dy = ((e.clientY - logoDragRef.current.startY) / rect.height) * 100;
    const rawX = Math.max(0, Math.min(100, logoDragRef.current.origX + dx));
    const rawY = Math.max(0, Math.min(100, logoDragRef.current.origY + dy));
    // Hold Shift to bypass snap
    const sx = e.shiftKey ? { value: rawX, hit: null } : snap(rawX);
    const sy = e.shiftKey ? { value: rawY, hit: null } : snap(rawY);
    setSnapGuides({ vx: sx.hit, hy: sy.hit });
    updateLogo({ x: sx.value, y: sy.value });
  };
  const onLogoDragEnd = () => {
    if (logoDragRef.current) {
      const pre = logoDragRef.current.preDrag;
      // Only snapshot if position actually moved
      if (pre.x !== state.logo.x || pre.y !== state.logo.y) snapshotLogoForUndo(pre);
    }
    logoDragRef.current = null;
    setSnapGuides({ vx: null, hy: null });
  };

  // ---- Custom Images (drag/upload onto flyer) ----
  const addImageFromSrc = (src: string) => {
    commit("image-add");
    const id = `img_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const newImg: ImageItem = {
      id, src, x: 50, y: 50, width: 30,
      scaleX: 1, scaleY: 1, rotation: 0, opacity: 1, borderRadius: 0,
    };
    setState(s => ({ ...s, images: [...s.images, newImg] }));
    setSelected(null);
    setSelectedImageId(id);
  };
  const onCustomImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach(f => {
      const reader = new FileReader();
      reader.onload = () => addImageFromSrc(reader.result as string);
      reader.readAsDataURL(f);
    });
    if (e.target) e.target.value = "";
  };
  const updateImage = (id: string, patch: Partial<ImageItem>, tag = "img") => {
    commit(`${tag}:${id}`);
    setState(s => ({ ...s, images: s.images.map(im => im.id === id ? { ...im, ...patch } : im) }));
  };
  const removeImage = (id: string) => {
    commit("image-remove");
    setState(s => ({ ...s, images: s.images.filter(im => im.id !== id) }));
    if (selectedImageId === id) setSelectedImageId(null);
  };
  const duplicateImage = (id: string) => {
    const src = state.images.find(im => im.id === id);
    if (!src) return;
    commit("image-dup");
    const newId = `img_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const copy: ImageItem = { ...src, id: newId, x: Math.min(95, src.x + 5), y: Math.min(95, src.y + 5) };
    setState(s => ({ ...s, images: [...s.images, copy] }));
    setSelectedImageId(newId);
  };
  const selectedImage = state.images.find(im => im.id === selectedImageId) || null;

  // Image drag on canvas
  const imageDragRef = useRef<{ id: string; startX: number; startY: number; origX: number; origY: number; committed: boolean } | null>(null);
  const onImageDragStart = (e: React.PointerEvent, id: string) => {
    e.stopPropagation();
    setSelected(null);
    setSelectedImageId(id);
    const im = state.images.find(x => x.id === id);
    if (!im) return;
    imageDragRef.current = { id, startX: e.clientX, startY: e.clientY, origX: im.x, origY: im.y, committed: false };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onImageDragMove = (e: React.PointerEvent) => {
    const ref = imageDragRef.current;
    if (!ref || !canvasRef.current) return;
    if (!ref.committed) { commit(`drag-img:${ref.id}`); ref.committed = true; }
    const rect = canvasRef.current.getBoundingClientRect();
    const dx = ((e.clientX - ref.startX) / rect.width) * 100;
    const dy = ((e.clientY - ref.startY) / rect.height) * 100;
    const rawX = Math.max(0, Math.min(100, ref.origX + dx));
    const rawY = Math.max(0, Math.min(100, ref.origY + dy));
    const sx = e.shiftKey ? { value: rawX, hit: null } : snap(rawX);
    const sy = e.shiftKey ? { value: rawY, hit: null } : snap(rawY);
    setSnapGuides({ vx: sx.hit, hy: sy.hit });
    setState(s => ({ ...s, images: s.images.map(im => im.id === ref.id ? { ...im, x: sx.value, y: sy.value } : im) }));
  };
  const onImageDragEnd = () => {
    imageDragRef.current = null;
    setSnapGuides({ vx: null, hy: null });
  };

  // Drop-from-OS onto canvas
  const onCanvasDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); }
  };
  const onCanvasDrop = (e: React.DragEvent) => {
    const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith("image/"));
    if (!files.length) return;
    e.preventDefault();
    files.forEach(f => {
      const reader = new FileReader();
      reader.onload = () => addImageFromSrc(reader.result as string);
      reader.readAsDataURL(f);
    });
  };


  const downloadPng = async () => {
    if (!canvasRef.current) return;
    try {
      const dataUrl = await toPng(canvasRef.current, { cacheBust: true, pixelRatio: 2 });
      const a = document.createElement("a");
      a.href = dataUrl; a.download = `flyer-${slugify(evt.title || "ofuesi")}.png`; a.click();
    } catch (e: any) {
      toast({ title: "Download failed", description: e.message, variant: "destructive" });
    }
  };
  const downloadPdf = async () => {
    if (!canvasRef.current) return;
    const dataUrl = await toPng(canvasRef.current, { cacheBust: true, pixelRatio: 2 });
    const pdf = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
    const w = pdf.internal.pageSize.getWidth(); const h = pdf.internal.pageSize.getHeight();
    pdf.addImage(dataUrl, "PNG", 24, 24, w - 48, h - 48, undefined, "FAST");
    pdf.save(`flyer-${slugify(evt.title || "ofuesi")}.pdf`);
  };

  // ---- Publish flyer to public Conference + Registration pages for this region ----
  const [publishing, setPublishing] = useState(false);
  const pushToConferencePage = async () => {
    if (!canvasRef.current || !evt.region) {
      toast({ title: "Cannot publish", description: "Missing flyer or region.", variant: "destructive" });
      return;
    }
    setPublishing(true);
    try {
      // 1. Render canvas to PNG blob
      const dataUrl = await toPng(canvasRef.current, { cacheBust: true, pixelRatio: 2 });
      const blob = await (await fetch(dataUrl)).blob();
      const regionSlug = slugify(evt.region);
      const ts = Date.now();
      const path = `published-flyers/${regionSlug}/${ts}.png`;

      // 2. Upload to public bucket
      const { error: upErr } = await supabase.storage
        .from("conference-kit-photos")
        .upload(path, blob, { contentType: "image/png", upsert: true });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("conference-kit-photos").getPublicUrl(path);
      const publicUrl = pub.publicUrl;

      // 3. Resolve region_id from region name
      let regionId = evt.region_id || null;
      if (!regionId) {
        const { data: regionRow, error: rErr } = await supabase
          .from("regions")
          .select("id,name")
          .ilike("name", evt.region.trim())
          .limit(1)
          .maybeSingle();
        if (rErr) throw rErr;
        if (!regionRow) throw new Error(`Region "${evt.region}" not found`);
        regionId = regionRow.id;
      }

      // 4. Update ONLY the upcoming 2026 conference for this region with the new flyer.
      //    Never touch completed/past rows — that historic flyers don't get overwritten
      //    and the new flyer doesn't surface under prior-year groupings.
      const { error: cErr } = await supabase
        .from("conferences")
        .update({ flyer_url: publicUrl })
        .eq("region_id", regionId)
        .eq("is_active", true)
        .eq("status", "upcoming")
        .ilike("conference_name", "%2026%");
      if (cErr) throw cErr;

      const kitUpdate = (supabase.from("conference_kit_events" as any) as any)
        .update({ flyer_url: publicUrl });
      const { error: kitErr } = await kitUpdate.eq("id", evt.id);
      if (kitErr) throw kitErr;

      // 5. Upsert flyer section in conference_content (drives registration page flyer tab)
      const { data: existing } = await (supabase.from("conference_content" as any) as any)
        .select("id")
        .eq("region_id", regionId)
        .eq("section", "flyer")
        .eq("conference_slug", "regional-conference-2026")
        .maybeSingle();
      if (existing?.id) {
        await (supabase.from("conference_content" as any) as any)
          .update({
            data: { image_url: publicUrl, url: publicUrl },
            body: evt.theme || null,
            title: evt.title || "Conference Flyer",
            is_active: true,
          })
          .eq("id", existing.id);
      } else {
        await (supabase.from("conference_content" as any) as any).insert({
          section: "flyer",
          region_id: regionId,
          title: evt.title || "Conference Flyer",
          body: evt.theme || null,
          data: { image_url: publicUrl, url: publicUrl },
          is_active: true,
          conference_slug: "regional-conference-2026",
        });
      }

      toast({
        title: "Flyer published",
        description: `Pushed to ${evt.region} conference & registration pages.`,
      });
    } catch (e: any) {
      toast({ title: "Publish failed", description: e.message || String(e), variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  };


  const sel = selected ? state.elements[selected] : null;

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-4">
      {/* Sidebar */}
      <div className="space-y-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Palette className="w-4 h-4"/>Templates</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              {TEMPLATES.map((t, i) => (
                <button key={t.id} onClick={() => switchTemplate(i)}
                  className={`relative aspect-[3/4] rounded-md border-2 overflow-hidden transition ${tplIdx === i ? "border-primary ring-2 ring-primary/30" : "border-border hover:border-primary/50"}`}
                  style={{ background: t.preview }}>
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] py-1 text-center">{t.name}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ImageIcon className="w-4 h-4"/>Background</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Label className="text-xs">Custom AI style hint (optional)</Label>
            <Input placeholder={tpl.styleHint} value={customStyle} onChange={e => setCustomStyle(e.target.value)} className="text-xs"/>
            <div className="flex gap-2">
              <Button size="sm" onClick={generateAIBg} disabled={aiBgLoading} className="flex-1">
                {aiBgLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin"/> : <Sparkles className="w-3 h-3 mr-1"/>}
                Generate AI BG
              </Button>
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}><Upload className="w-3 h-3 mr-1"/>Upload</Button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload}/>
            </div>
            {state.bgImage && (
              <Button size="sm" variant="ghost" onClick={() => setState(s => ({ ...s, bgImage: undefined }))} className="w-full text-xs">
                <RefreshCw className="w-3 h-3 mr-1"/>Clear background image
              </Button>
            )}
            <div>
              <Label className="text-xs">Overlay darkness: {Math.round(state.bgOverlay * 100)}%</Label>
              <Slider value={[state.bgOverlay * 100]} max={80} step={5} onValueChange={v => setState(s => ({ ...s, bgOverlay: v[0] / 100 }))}/>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><ImageIcon className="w-4 h-4"/>Logo</CardTitle>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className="h-7 px-2" disabled={!logoPast.length} onClick={undoLogo} title="Undo (⌘Z)">
                <Undo2 className="w-3.5 h-3.5"/>
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" disabled={!logoFuture.length} onClick={redoLogo} title="Redo (⌘⇧Z)">
                <Redo2 className="w-3.5 h-3.5"/>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 rounded-md bg-white/80 border flex items-center justify-center overflow-hidden flex-shrink-0">
                {state.logo.src ? <img src={state.logo.src} alt="logo" className="max-w-full max-h-full object-contain"/> : <ImageIcon className="w-4 h-4 text-muted-foreground"/>}
              </div>
              <div className="flex-1 grid grid-cols-1 gap-1">
                <div className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 text-center">
                  OFUESI logo is locked
                </div>
                <Button size="sm" variant="outline" className="text-xs" onClick={() => { commitLogo(state.logo); updateLogo({ src: ofuesiLogo, visible: true }); }}>
                  <RefreshCw className="w-3 h-3 mr-1"/>Reapply OFUESI logo
                </Button>
                <input ref={logoFileRef} type="file" accept="image/*" hidden onChange={onLogoUpload}/>
                <Button size="sm" variant="outline" className="text-xs" onClick={() => { commitLogo(state.logo); updateLogo({ ...DEFAULT_LOGO, ...(tpl.defaultLogo || {}), src: ofuesiLogo }); }}>
                  <RefreshCw className="w-3 h-3 mr-1"/>Reset to template default
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs flex-1">Show logo</Label>
              <input type="checkbox" checked={state.logo.visible} onChange={e => { commitLogo(state.logo); updateLogo({ visible: e.target.checked }); }}/>
            </div>
            <div>
              <Label className="text-xs">Base size: {state.logo.size}%</Label>
              <Slider
                value={[state.logo.size]} min={5} max={90} step={1}
                onValueChange={v => updateLogo({ size: v[0] })}
                onValueCommit={() => snapshotLogoForUndo({ ...state.logo, size: state.logo.size })}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Scale X: {state.logo.scaleX.toFixed(2)}×</Label>
                <Slider
                  value={[state.logo.scaleX * 100]} min={20} max={300} step={5}
                  onValueChange={v => updateLogo({ scaleX: v[0] / 100 })}
                  onValueCommit={() => snapshotLogoForUndo(state.logo)}
                />
              </div>
              <div>
                <Label className="text-xs">Scale Y: {state.logo.scaleY.toFixed(2)}×</Label>
                <Slider
                  value={[state.logo.scaleY * 100]} min={20} max={300} step={5}
                  onValueChange={v => updateLogo({ scaleY: v[0] / 100 })}
                  onValueCommit={() => snapshotLogoForUndo(state.logo)}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><RotateCw className="w-3 h-3"/>Rotation: {state.logo.rotation}°</Label>
              <Slider
                value={[state.logo.rotation]} min={-180} max={180} step={1}
                onValueChange={v => updateLogo({ rotation: v[0] })}
                onValueCommit={() => snapshotLogoForUndo(state.logo)}
              />
              <div className="flex gap-1 mt-1">
                {[0, 90, 180, -90].map(d => (
                  <Button key={d} size="sm" variant="outline" className="h-6 text-[10px] flex-1" onClick={() => { commitLogo(state.logo); updateLogo({ rotation: d }); }}>{d}°</Button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Opacity: {Math.round(state.logo.opacity * 100)}%</Label>
              <Slider
                value={[state.logo.opacity * 100]} min={10} max={100} step={5}
                onValueChange={v => updateLogo({ opacity: v[0] / 100 })}
                onValueCommit={() => snapshotLogoForUndo(state.logo)}
              />
            </div>
            <p className="text-[10px] text-muted-foreground">Drag the logo on the canvas. Snaps to edges, thirds & center — hold Shift to bypass.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Type className="w-4 h-4"/>Content & AI Copy</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Button size="sm" variant="outline" onClick={generateAICopy} disabled={aiCopyLoading} className="w-full">
              {aiCopyLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin"/> : <Sparkles className="w-3 h-3 mr-1"/>}
              Refine theme & scripture with AI
            </Button>
            <div className="grid grid-cols-2 gap-1.5">
              <Input placeholder="Title" value={evt.title || ""} onChange={e => setEvt(v => ({ ...v, title: e.target.value }))} className="text-xs col-span-2"/>
              <Input placeholder="Theme" value={evt.theme || ""} onChange={e => setEvt(v => ({ ...v, theme: e.target.value }))} className="text-xs col-span-2"/>
              <Input placeholder="Scripture" value={evt.scripture || ""} onChange={e => setEvt(v => ({ ...v, scripture: e.target.value }))} className="text-xs col-span-2"/>
              <Input type="date" value={evt.start_date || ""} onChange={e => setEvt(v => ({ ...v, start_date: e.target.value }))} className="text-xs"/>
              <Input placeholder="Time" value={evt.start_time || ""} onChange={e => setEvt(v => ({ ...v, start_time: e.target.value }))} className="text-xs"/>
              <Input placeholder="Venue" value={evt.venue_name || ""} onChange={e => setEvt(v => ({ ...v, venue_name: e.target.value }))} className="text-xs"/>
              <Input placeholder="City" value={evt.city || ""} onChange={e => setEvt(v => ({ ...v, city: e.target.value }))} className="text-xs"/>
              <Input placeholder="Speaker" value={evt.speaker_name || ""} onChange={e => setEvt(v => ({ ...v, speaker_name: e.target.value }))} className="text-xs col-span-2"/>
            </div>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-purple-50/40">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600"/>GenAI Design Assistant</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Textarea
              value={aiDesignPrompt}
              onChange={(e) => setAiDesignPrompt(e.target.value)}
              placeholder='e.g. "Change accent color to royal blue", "Shorten the title", "Hide the scripture line", "Regenerate background with mountain sunrise"'
              rows={3}
              className="text-xs"
              disabled={aiDesignLoading}
            />
            <Button size="sm" onClick={runAIDesign} disabled={aiDesignLoading || !aiDesignPrompt.trim()} className="w-full">
              {aiDesignLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin"/> : <Sparkles className="w-3 h-3 mr-1"/>}
              Apply with AI
            </Button>
            {aiDesignNote && (
              <p className="text-[10px] text-purple-800 bg-purple-100 rounded px-2 py-1">{aiDesignNote}</p>
            )}
            <p className="text-[10px] text-muted-foreground">Edits texts, colors, visibility, or regenerates background. Undo (⌘Z) reverts any AI change.</p>
          </CardContent>
        </Card>

        {/* Custom images on canvas */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><ImagePlus className="w-4 h-4"/>Images on flyer</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button size="sm" variant="outline" className="w-full" onClick={() => imageFileRef.current?.click()}>
              <Upload className="w-3 h-3 mr-1"/>Upload image(s)
            </Button>
            <input ref={imageFileRef} type="file" accept="image/*" multiple hidden onChange={onCustomImageUpload}/>
            <p className="text-[10px] text-muted-foreground">Or drag image files directly onto the flyer canvas →</p>

            {state.images.length === 0 && (
              <p className="text-[11px] text-muted-foreground italic">No images added yet.</p>
            )}

            {state.images.length > 0 && (
              <div className="space-y-1.5 max-h-48 overflow-auto pr-1">
                {state.images.map((im, i) => (
                  <div key={im.id}
                    className={`flex items-center gap-2 p-1.5 rounded border ${selectedImageId === im.id ? "border-primary bg-primary/5" : "border-border"}`}
                    onClick={() => { setSelected(null); setSelectedImageId(im.id); }}>
                    <img src={im.src} alt="" className="w-8 h-8 object-cover rounded border bg-white"/>
                    <span className="text-[11px] flex-1 truncate">Image {i + 1}</span>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); duplicateImage(im.id); }} title="Duplicate">
                      <CopyIcon className="w-3 h-3"/>
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={(e) => { e.stopPropagation(); removeImage(im.id); }} title="Delete">
                      <Trash2 className="w-3 h-3"/>
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {selectedImage && (
              <div className="space-y-2 pt-2 border-t">
                <div className="text-[11px] font-medium">Edit selected image</div>
                <div>
                  <Label className="text-xs">Width: {selectedImage.width}%</Label>
                  <Slider value={[selectedImage.width]} min={5} max={100} step={1}
                    onValueChange={v => updateImage(selectedImage.id, { width: v[0] }, "img-w")}/>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Scale X: {selectedImage.scaleX.toFixed(2)}×</Label>
                    <Slider value={[selectedImage.scaleX * 100]} min={20} max={300} step={5}
                      onValueChange={v => updateImage(selectedImage.id, { scaleX: v[0] / 100 }, "img-sx")}/>
                  </div>
                  <div>
                    <Label className="text-xs">Scale Y: {selectedImage.scaleY.toFixed(2)}×</Label>
                    <Slider value={[selectedImage.scaleY * 100]} min={20} max={300} step={5}
                      onValueChange={v => updateImage(selectedImage.id, { scaleY: v[0] / 100 }, "img-sy")}/>
                  </div>
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1"><RotateCw className="w-3 h-3"/>Rotation: {selectedImage.rotation}°</Label>
                  <Slider value={[selectedImage.rotation]} min={-180} max={180} step={1}
                    onValueChange={v => updateImage(selectedImage.id, { rotation: v[0] }, "img-r")}/>
                </div>
                <div>
                  <Label className="text-xs">Opacity: {Math.round(selectedImage.opacity * 100)}%</Label>
                  <Slider value={[selectedImage.opacity * 100]} min={10} max={100} step={5}
                    onValueChange={v => updateImage(selectedImage.id, { opacity: v[0] / 100 }, "img-o")}/>
                </div>
                <div>
                  <Label className="text-xs">Corner radius: {selectedImage.borderRadius}px</Label>
                  <Slider value={[selectedImage.borderRadius]} min={0} max={200} step={2}
                    onValueChange={v => updateImage(selectedImage.id, { borderRadius: v[0] }, "img-br")}/>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {sel && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Type className="w-4 h-4"/>Edit text: <span className="capitalize text-primary">{selected}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Always-visible top row: visibility + quick style toggles */}
              <div className="flex items-center gap-1 flex-wrap">
                <label className="flex items-center gap-1 text-xs mr-2">
                  <input type="checkbox" checked={sel.visible} onChange={e => updateEl(selected!, { visible: e.target.checked })}/>
                  Visible
                </label>
                <Button size="sm" variant={sel.fontWeight >= 700 ? "default" : "outline"} className="h-7 w-7 p-0" title="Bold"
                  onClick={() => updateEl(selected!, { fontWeight: sel.fontWeight >= 700 ? 400 : 700 })}><Bold className="w-3 h-3"/></Button>
                <Button size="sm" variant={sel.italic ? "default" : "outline"} className="h-7 w-7 p-0" title="Italic"
                  onClick={() => updateEl(selected!, { italic: !sel.italic })}><ItalicIcon className="w-3 h-3"/></Button>
                <Button size="sm" variant={sel.underline ? "default" : "outline"} className="h-7 w-7 p-0" title="Underline"
                  onClick={() => updateEl(selected!, { underline: !sel.underline })}><UnderlineIcon className="w-3 h-3"/></Button>
                <Button size="sm" variant={sel.uppercase ? "default" : "outline"} className="h-7 w-7 p-0" title="Uppercase"
                  onClick={() => updateEl(selected!, { uppercase: !sel.uppercase })}><CaseUpper className="w-3 h-3"/></Button>
                <Separator orientation="vertical" className="h-6 mx-1"/>
                <Button size="sm" variant={sel.align === "left" ? "default" : "outline"} className="h-7 w-7 p-0" title="Align left"
                  onClick={() => updateEl(selected!, { align: "left" })}><AlignLeft className="w-3 h-3"/></Button>
                <Button size="sm" variant={sel.align === "center" ? "default" : "outline"} className="h-7 w-7 p-0" title="Align center"
                  onClick={() => updateEl(selected!, { align: "center" })}><AlignCenter className="w-3 h-3"/></Button>
                <Button size="sm" variant={sel.align === "right" ? "default" : "outline"} className="h-7 w-7 p-0" title="Align right"
                  onClick={() => updateEl(selected!, { align: "right" })}><AlignRight className="w-3 h-3"/></Button>
                <Button size="sm" variant={sel.align === "justify" ? "default" : "outline"} className="h-7 w-7 p-0" title="Justify"
                  onClick={() => updateEl(selected!, { align: "justify" })}><AlignJustify className="w-3 h-3"/></Button>
                <Button size="sm" variant="ghost" className="ml-auto h-7 text-[11px]"
                  onClick={() => updateEl(selected!, { text: undefined })} title="Reset to auto text">
                  <RotateCcw className="w-3 h-3 mr-1"/>Auto
                </Button>
              </div>

              <Accordion type="multiple" defaultValue={["content", "typography"]} className="w-full">
                <AccordionItem value="content">
                  <AccordionTrigger className="text-xs py-2"><span className="flex items-center gap-2"><Type className="w-3.5 h-3.5"/>Content</span></AccordionTrigger>
                  <AccordionContent className="space-y-2">
                    <Textarea
                      value={sel.text ?? buildText(selected!, evt, regUrl) ?? ""}
                      onChange={e => updateEl(selected!, { text: e.target.value })}
                      placeholder="Type your text…"
                      className="text-xs min-h-[64px]"
                    />
                    <p className="text-[10px] text-muted-foreground">Leave empty or click Auto to restore generated text.</p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="typography">
                  <AccordionTrigger className="text-xs py-2"><span className="flex items-center gap-2"><Type className="w-3.5 h-3.5"/>Typography</span></AccordionTrigger>
                  <AccordionContent className="space-y-2">
                    <div>
                      <Label className="text-xs">Font family</Label>
                      <Select value={sel.fontFamily} onValueChange={v => updateEl(selected!, { fontFamily: v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue/></SelectTrigger>
                        <SelectContent>{FONTS.map(f => <SelectItem key={f} value={f} style={{ fontFamily: f }}>{f.split(",")[0].replace(/'/g, "")}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Size: {sel.fontSize}px</Label>
                        <Slider value={[sel.fontSize]} min={8} max={120} step={1} onValueChange={v => updateEl(selected!, { fontSize: v[0] })}/>
                      </div>
                      <div>
                        <Label className="text-xs">Weight: {sel.fontWeight}</Label>
                        <Slider value={[sel.fontWeight]} min={300} max={900} step={100} onValueChange={v => updateEl(selected!, { fontWeight: v[0] })}/>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Line height: {(sel.lineHeight ?? 1.2).toFixed(2)}</Label>
                        <Slider value={[(sel.lineHeight ?? 1.2) * 100]} min={80} max={250} step={5} onValueChange={v => updateEl(selected!, { lineHeight: v[0] / 100 })}/>
                      </div>
                      <div>
                        <Label className="text-xs">Letter spacing: {sel.letterSpacing ?? 0}px</Label>
                        <Slider value={[sel.letterSpacing ?? 0]} min={-2} max={20} step={1} onValueChange={v => updateEl(selected!, { letterSpacing: v[0] })}/>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="color">
                  <AccordionTrigger className="text-xs py-2"><span className="flex items-center gap-2"><Droplet className="w-3.5 h-3.5"/>Color & effects</span></AccordionTrigger>
                  <AccordionContent className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Text color</Label>
                        <Input type="color" value={sel.color} onChange={e => updateEl(selected!, { color: e.target.value }, { tag: `color:${selected}` })} className="h-8 p-1"/>
                      </div>
                      <div>
                        <Label className="text-xs">Pill background</Label>
                        <Input type="color" value={sel.bg || "#000000"} onChange={e => updateEl(selected!, { bg: e.target.value }, { tag: `bg:${selected}` })} className="h-8 p-1"/>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Color opacity: {Math.round((sel.colorOpacity ?? 1) * 100)}%</Label>
                      <Slider value={[(sel.colorOpacity ?? 1) * 100]} min={0} max={100} step={5} onValueChange={v => updateEl(selected!, { colorOpacity: v[0] / 100 }, { tag: `cop:${selected}` })}/>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant={sel.bg ? "default" : "outline"} className="flex-1 h-7 text-xs"
                        onClick={() => updateEl(selected!, { bg: sel.bg ? undefined : tpl.defaultAccent })}>
                        {sel.bg ? "Pill ON" : "Pill OFF"}
                      </Button>
                      <Button size="sm" variant={sel.shadow ? "default" : "outline"} className="flex-1 h-7 text-xs"
                        onClick={() => updateEl(selected!, { shadow: !sel.shadow })}>
                        {sel.shadow ? "Shadow ON" : "Shadow OFF"}
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="alignment">
                  <AccordionTrigger className="text-xs py-2"><span className="flex items-center gap-2"><AlignCenter className="w-3.5 h-3.5"/>Alignment</span></AccordionTrigger>
                  <AccordionContent className="space-y-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Horizontal</Label>
                      <div className="flex gap-1 mt-1">
                        {(["left", "center", "right", "justify"] as const).map(a => (
                          <Button key={a} size="sm" variant={sel.align === a ? "default" : "outline"} className="flex-1 h-7 text-[11px]" onClick={() => updateEl(selected!, { align: a })}>{a}</Button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Vertical</Label>
                      <div className="flex gap-1 mt-1">
                        {(["top", "center", "bottom"] as const).map(v => (
                          <Button key={v} size="sm" variant={(sel.vAlign ?? "center") === v ? "default" : "outline"} className="flex-1 h-7 text-xs" onClick={() => updateEl(selected!, { vAlign: v })}>{v}</Button>
                        ))}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="sizing">
                  <AccordionTrigger className="text-xs py-2"><span className="flex items-center gap-2"><Ruler className="w-3.5 h-3.5"/>Sizing & spacing</span></AccordionTrigger>
                  <AccordionContent className="space-y-2">
                    <div>
                      <Label className="text-xs">Block width: {sel.width}%</Label>
                      <Slider value={[sel.width]} min={20} max={100} step={1} onValueChange={v => updateEl(selected!, { width: v[0] })}/>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Padding top: {sel.paddingTop ?? 0}px</Label>
                        <Slider value={[sel.paddingTop ?? 0]} min={0} max={80} step={1} onValueChange={v => updateEl(selected!, { paddingTop: v[0] }, { tag: `pt:${selected}` })}/>
                      </div>
                      <div>
                        <Label className="text-xs">Padding bottom: {sel.paddingBottom ?? 0}px</Label>
                        <Slider value={[sel.paddingBottom ?? 0]} min={0} max={80} step={1} onValueChange={v => updateEl(selected!, { paddingBottom: v[0] }, { tag: `pb:${selected}` })}/>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 items-end">
                      <div>
                        <Label className="text-xs">Max lines: {sel.maxLines || "unlimited"}</Label>
                        <Slider value={[sel.maxLines ?? 0]} min={0} max={10} step={1} onValueChange={v => updateEl(selected!, { maxLines: v[0] || undefined }, { tag: `ml:${selected}` })}/>
                      </div>
                      <Button size="sm" variant={sel.autoFit ? "default" : "outline"} className="h-8 text-xs" onClick={() => updateEl(selected!, { autoFit: !sel.autoFit })}>
                        {sel.autoFit ? "Auto-fit ON" : "Auto-fit OFF"}
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        )}


        <div className="flex gap-2">
          <Button onClick={downloadPng} className="flex-1"><FileImage className="w-4 h-4 mr-1"/>PNG</Button>
          <Button onClick={downloadPdf} variant="outline" className="flex-1"><FileText className="w-4 h-4 mr-1"/>PDF</Button>
        </div>
        <Button
          onClick={pushToConferencePage}
          disabled={publishing}
          variant="default"
          className="w-full bg-primary hover:bg-primary/90"
        >
          {publishing ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Upload className="w-4 h-4 mr-1"/>}
          Push to Conference Page ({evt.region})
        </Button>
      </div>

      {/* Canvas */}
      <div className="space-y-2">
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Move className="w-3 h-3"/>Click any element to edit · drag to reposition · drop image files onto the canvas to add
        </div>
        <div className="bg-neutral-200 dark:bg-neutral-900 rounded-lg p-4 flex justify-center overflow-auto">
          <div
            ref={canvasRef}
            onPointerMove={(e) => { onDragMove(e); onLogoDragMove(e); onImageDragMove(e); }}
            onPointerUp={() => { onDragEnd(); onLogoDragEnd(); onImageDragEnd(); }}
            onClick={() => { setSelected(null); setSelectedImageId(null); }}
            onDragOver={onCanvasDragOver}
            onDrop={onCanvasDrop}
            className="relative shadow-2xl"
            style={{
              width: 600, height: 800,
              background: state.bgImage ? `url(${state.bgImage}) center/cover no-repeat` : tpl.bgGradient,
            }}
          >
            {/* Overlay */}
            {state.bgImage && state.bgOverlay > 0 && (
              <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: `rgba(0,0,0,${state.bgOverlay})` }}/>
            )}

            {/* Logo */}
            {state.logo.visible && state.logo.src && (
              <img
                src={state.logo.src}
                alt="Logo"
                onPointerDown={onLogoDragStart}
                onClick={(e) => e.stopPropagation()}
                draggable={false}
                className="absolute cursor-move select-none touch-none hover:outline hover:outline-1 hover:outline-cyan-400/60"
                style={{
                  left: `${state.logo.x}%`,
                  top: `${state.logo.y}%`,
                  width: `${state.logo.size}%`,
                  transform: `translate(-50%, -50%) rotate(${state.logo.rotation}deg) scale(${state.logo.scaleX}, ${state.logo.scaleY})`,
                  transformOrigin: "center",
                  opacity: state.logo.opacity,
                  objectFit: "contain",
                  background: "transparent",
                  borderRadius: 0,
                  padding: 0,
                }}
              />
            )}

            {/* Custom images */}
            {state.images.map((im) => {
              const isSel = selectedImageId === im.id;
              return (
                <img
                  key={im.id}
                  src={im.src}
                  alt=""
                  onPointerDown={(e) => onImageDragStart(e, im.id)}
                  onClick={(e) => { e.stopPropagation(); setSelected(null); setSelectedImageId(im.id); }}
                  draggable={false}
                  className={`absolute cursor-move select-none touch-none ${isSel ? "outline outline-2 outline-cyan-400 outline-dashed" : "hover:outline hover:outline-1 hover:outline-cyan-400/60"}`}
                  style={{
                    left: `${im.x}%`,
                    top: `${im.y}%`,
                    width: `${im.width}%`,
                    transform: `translate(-50%, -50%) rotate(${im.rotation}deg) scale(${im.scaleX}, ${im.scaleY})`,
                    transformOrigin: "center",
                    opacity: im.opacity,
                    objectFit: "contain",
                    borderRadius: im.borderRadius,
                  }}
                />
              );
            })}

            {/* Snap guides */}
            {snapGuides.vx !== null && (
              <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${snapGuides.vx}%`, width: 1, background: "rgba(34,211,238,0.9)", boxShadow: "0 0 6px rgba(34,211,238,0.7)" }}/>
            )}
            {snapGuides.hy !== null && (
              <div className="absolute left-0 right-0 pointer-events-none" style={{ top: `${snapGuides.hy}%`, height: 1, background: "rgba(34,211,238,0.9)", boxShadow: "0 0 6px rgba(34,211,238,0.7)" }}/>
            )}

            {/* Elements */}
            {(Object.keys(state.elements) as ElKey[]).map(key => {
              const el = state.elements[key];
              if (!el.visible) return null;
              const text = el.text || buildText(key, evt, regUrl);
              if (!text) return null;
              const isSel = selected === key;
              const colorVal = el.colorOpacity !== undefined && el.colorOpacity < 1
                ? hexToRgba(el.color, el.colorOpacity)
                : el.color;
              const padTop = el.paddingTop ?? 0;
              const padBot = el.paddingBottom ?? 0;
              const pillPadX = el.bg ? 18 : 0;
              const clampStyle: React.CSSProperties = el.maxLines
                ? { display: "-webkit-box", WebkitLineClamp: el.maxLines, WebkitBoxOrient: "vertical" as any, overflow: "hidden" }
                : {};
              return (
                <div
                  key={key}
                  ref={(node) => { elRefs.current[key] = node; }}
                  data-fit-key={key}
                  data-fit-base={el.fontSize}
                  data-fit-on={el.autoFit ? "1" : "0"}
                  data-fit-maxlines={el.maxLines ?? 0}
                  onPointerDown={e => onDragStart(e, key)}
                  onClick={e => { e.stopPropagation(); setSelected(key); }}
                  className={`absolute cursor-move select-none ${isSel ? "outline outline-2 outline-cyan-400 outline-dashed" : "hover:outline hover:outline-1 hover:outline-white/40"}`}
                  style={{
                    left: `${el.x}%`,
                    top: `${el.y}%`,
                    width: `${el.width}%`,
                    transform: `translate(-50%, ${el.vAlign === "top" ? "0%" : el.vAlign === "bottom" ? "-100%" : "-50%"})`,
                    textAlign: el.align,
                    color: colorVal,
                    fontFamily: el.fontFamily,
                    fontSize: el.fontSize,
                    fontWeight: el.fontWeight,
                    fontStyle: el.italic ? "italic" : "normal",
                    textDecoration: el.underline ? "underline" : undefined,
                    textTransform: el.uppercase ? "uppercase" : undefined,
                    letterSpacing: el.letterSpacing ? `${el.letterSpacing}px` : undefined,
                    textShadow: el.shadow ? "0 2px 12px rgba(0,0,0,0.6)" : undefined,
                    lineHeight: el.lineHeight ?? 1.2,
                    paddingTop: el.bg ? Math.max(8, padTop) : padTop || undefined,
                    paddingBottom: el.bg ? Math.max(8, padBot) : padBot || undefined,
                    paddingLeft: pillPadX || undefined,
                    paddingRight: pillPadX || undefined,
                    backgroundColor: el.bg,
                    borderRadius: el.bg ? 999 : undefined,
                    display: el.bg ? "inline-block" : "block",
                    whiteSpace: el.align === "justify" ? "normal" : "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    hyphens: el.align === "justify" ? "auto" : undefined,
                    ...clampStyle,
                  }}
                >
                  {text}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlyerBuilderPro;
